import React from "react";
import { Paper, Box, Typography, Switch, Stack, Divider } from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import NotificationsIcon from "@mui/icons-material/Notifications";
import SmsIcon from "@mui/icons-material/Sms";
import {
  ClientEmailNotifications,
  ClientPushNotifications,
  ClientSmsNotifications,
} from "shared/types";

interface NotificationsSectionProps {
  emailNotifications: ClientEmailNotifications;
  pushNotifications: ClientPushNotifications;
  smsNotifications: ClientSmsNotifications;
  onEmailChange: (key: keyof ClientEmailNotifications, value: boolean) => void;
  onPushChange: (key: keyof ClientPushNotifications, value: boolean) => void;
  onSmsChange: (key: keyof ClientSmsNotifications, value: boolean) => void;
}

const switchSx = {
  "& .MuiSwitch-switchBase.Mui-checked": { color: "#178B8E" },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: "#178B8E",
  },
};

interface SwitchRowProps {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}

const SwitchRow: React.FC<SwitchRowProps> = ({
  label,
  description,
  checked,
  onChange,
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      py: 1.25,
    }}
  >
    <Box>
      <Typography variant="body2" fontWeight={500}>
        {label}
      </Typography>
      {description && (
        <Typography variant="caption" color="text.secondary">
          {description}
        </Typography>
      )}
    </Box>
    <Switch
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      size="small"
      sx={switchSx}
    />
  </Box>
);

const GroupHeader: React.FC<{ icon: React.ReactNode; title: string }> = ({
  icon,
  title,
}) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
    <Box sx={{ color: "#178B8E", display: "flex" }}>{icon}</Box>
    <Typography variant="body1" fontWeight={600}>
      {title}
    </Typography>
  </Box>
);

const NotificationsSection: React.FC<NotificationsSectionProps> = ({
  emailNotifications,
  pushNotifications,
  smsNotifications,
  onEmailChange,
  onPushChange,
  onSmsChange,
}) => {
  return (
    <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
      <Typography variant="h6" fontWeight={600} mb={0.5}>
        Notifications
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Choose what you want to be notified about
      </Typography>

      {/* Email */}
      <GroupHeader
        icon={<EmailIcon fontSize="small" />}
        title="Email Notifications"
      />
      <Stack divider={<Divider />}>
        <SwitchRow
          label="New booking requests"
          checked={emailNotifications.bookingRequests}
          onChange={(v) => onEmailChange("bookingRequests", v)}
        />
        <SwitchRow
          label="Booking confirmations"
          checked={emailNotifications.confirmations}
          onChange={(v) => onEmailChange("confirmations", v)}
        />
        <SwitchRow
          label="Booking reminders"
          checked={emailNotifications.reminders}
          onChange={(v) => onEmailChange("reminders", v)}
        />
        <SwitchRow
          label="Messages from sitters"
          checked={emailNotifications.messages}
          onChange={(v) => onEmailChange("messages", v)}
        />
        <SwitchRow
          label="Payment receipts"
          checked={emailNotifications.receipts}
          onChange={(v) => onEmailChange("receipts", v)}
        />
        <SwitchRow
          label="Special offers & updates"
          description="Promotions and news"
          checked={emailNotifications.offers}
          onChange={(v) => onEmailChange("offers", v)}
        />
      </Stack>

      <Divider sx={{ my: 2.5 }} />

      {/* Push */}
      <GroupHeader
        icon={<NotificationsIcon fontSize="small" />}
        title="Push Notifications"
      />
      <Stack divider={<Divider />}>
        <SwitchRow
          label="New messages"
          checked={pushNotifications.messages}
          onChange={(v) => onPushChange("messages", v)}
        />
        <SwitchRow
          label="Booking updates"
          checked={pushNotifications.bookings}
          onChange={(v) => onPushChange("bookings", v)}
        />
        <SwitchRow
          label="Reminders"
          checked={pushNotifications.reminders}
          onChange={(v) => onPushChange("reminders", v)}
        />
      </Stack>

      <Divider sx={{ my: 2.5 }} />

      {/* SMS */}
      <GroupHeader
        icon={<SmsIcon fontSize="small" />}
        title="SMS Notifications"
      />
      <Stack divider={<Divider />}>
        <SwitchRow
          label="Critical updates only"
          description="Urgent booking changes and account alerts"
          checked={smsNotifications.criticalOnly}
          onChange={(v) => onSmsChange("criticalOnly", v)}
        />
      </Stack>
    </Paper>
  );
};

export default NotificationsSection;
